import { useState, useEffect, useMemo } from 'react';
import { Mail, Reply, AtSign, CreditCard, Check, AlertTriangle, Info } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { fetchDailySendStats, fetchActivities } from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { useTranslation } from 'react-i18next';
import { getAliasUpgradeTier, getEffectivePlanName } from './planPrompts';
import { formatActivityDetails, formatActivitySender, processActivities } from '../../utils/activity';
import { getUsageColor, getUsagePercentage, type UsageColor } from './usageMetrics';

const Dashboard = () => {
  const { t } = useTranslation();
  const { userInfo, loading: userLoading, refetchUserInfo } = useUser();
  const [quotaInfo, setQuotaInfo] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [showAliasUpgradeBanner, setShowAliasUpgradeBanner] = useState(true);

  const processedActivities = useMemo(() => {
    return processActivities(activities, {
      received: t('activities.received', '收到'),
      replied: t('activities.replied', '回复'),
    });
  }, [activities, t]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, activitiesData] = await Promise.all([
          fetchDailySendStats({ force: true }),
          fetchActivities(),
          refetchUserInfo(),
        ]);
        setQuotaInfo(statsData);
        if (activitiesData && activitiesData.result) {
          setActivities(activitiesData.result);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching stats:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [refetchUserInfo]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!loading && !userLoading && params.get('upgrade') === 'plus') {
      setIsPlanModalOpen(true);
    }
  }, [loading, userLoading]);

  if (loading || userLoading) {
    return (
      <DashboardLayout 
        isPlanModalOpen={isPlanModalOpen}
        onPlanModalClose={() => setIsPlanModalOpen(false)}
        onOpenPlanModal={() => setIsPlanModalOpen(true)}
      >
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!userInfo?.name || !userInfo?.addr) {
    return <Navigate to="/dashboard/settings" replace />;
  }

  const aliasCount = userInfo?.aliasCount || 0;
  const summaryCards = [
    {
      icon: <Mail className="h-6 w-6 text-blue-600" />,
      label: t('dashboard.stats.totalAliases'),
      value: userInfo?.limits.aliasLimits >= 0
        ? `${userInfo?.aliasCount?.toString() || '0'}/${userInfo.limits.aliasLimits}`
        : `${userInfo?.aliasCount?.toString() || '0'}/${t('common.unlimited', 'Unlimited')}`,
    },
    {
      icon: <Check className="h-6 w-6 text-emerald-600" />,
      label: t('dashboard.stats.protectedSignups'),
      value: t('dashboard.stats.protectedSignupsValue', { count: aliasCount }),
    },
    {
      icon: <Reply className="h-6 w-6 text-green-600" />,
      label: t('dashboard.stats.emailsForwarded'),
      value: userInfo?.fwd?.toString() || '0',
    },
    {
      icon: <Reply className="h-6 w-6 text-purple-600" />,
      label: t('dashboard.stats.repliesSent'),
      value: userInfo?.rpy?.toString() || '0',
    },
  ];

  const dailyQuota = userInfo?.limits.dailylimits;
  const monthlyQuota = userInfo?.limits.monthlyLimits;
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${now.getMonth() + 1}`;
  const dailyUsage = quotaInfo?.result?.times?.main_total || 0;
  const monthlyUsage = currentMonth === userInfo?.curMonth ? userInfo?.month_total || 0 : 0;
  const dailyUsagePercentage = getUsagePercentage(dailyUsage, dailyQuota);
  const monthlyUsagePercentage = getUsagePercentage(monthlyUsage, monthlyQuota);
  const dailyUsageColor = getUsageColor(dailyUsagePercentage);
  const monthlyUsageColor = getUsageColor(monthlyUsagePercentage);
  const progressBarColors = {
    green: 'bg-green-600',
    yellow: 'bg-yellow-500',
    red: 'bg-red-600'
  } satisfies Record<UsageColor, string>;
  const textColors = {
    green: 'text-green-600',
    yellow: 'text-yellow-500',
    red: 'text-red-600'
  } satisfies Record<UsageColor, string>;
  const aliasUpgradeTier = getAliasUpgradeTier(userInfo);
  const effectivePlanName = getEffectivePlanName(userInfo);
  const usageCards = [
    {
      title: t('dashboard.stats.dailyUsage'),
      used: dailyUsage,
      total: dailyQuota,
      percentage: dailyUsagePercentage,
      color: dailyUsageColor,
    },
    {
      title: t('dashboard.stats.monthlyUsage', { month: currentMonth }),
      used: monthlyUsage,
      total: monthlyQuota,
      percentage: monthlyUsagePercentage,
      color: monthlyUsageColor,
    },
  ];

  return (
    <DashboardLayout 
      isPlanModalOpen={isPlanModalOpen}
      onPlanModalClose={() => setIsPlanModalOpen(false)}
      onOpenPlanModal={() => setIsPlanModalOpen(true)}
    >
      <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {t('dashboard.welcome', { name: userInfo.name })}
            </h1>
            <p className="text-gray-600 mt-2">
              {t('dashboard.email', { email: userInfo.addr })}
            </p>
          </div>
          {effectivePlanName !== 'vip' && (
            <button
              onClick={() => setIsPlanModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {t('dashboard.upgrade')}
            </button>
          )}
        </div>
        {effectivePlanName === 'vip' && (
          <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {t('dashboard.vipMember')}
          </div>
        )}
      </div>

      {aliasUpgradeTier === 'a' && showAliasUpgradeBanner && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-5 py-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-md bg-amber-100 p-2 text-amber-700">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-amber-950">
                  {t('dashboard.aliasUpgradeBanner.using', { count: userInfo?.aliasCount || 0 })}
                </p>
                <p className="text-sm text-amber-900">
                  {t('dashboard.aliasUpgradeBanner.freeLimit')}
                </p>
                <p className="text-sm text-amber-900">
                  {t('dashboard.aliasUpgradeBanner.upgradePrompt')}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() => setIsPlanModalOpen(true)}
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                {t('dashboard.aliasUpgradeBanner.upgradeNow')}
              </button>
              <button
                onClick={() => setShowAliasUpgradeBanner(false)}
                className="inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-100"
              >
                {t('dashboard.aliasUpgradeBanner.maybeLater')}
              </button>
            </div>
          </div>
        </div>
      )}

      {aliasUpgradeTier === 'b' && showAliasUpgradeBanner && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-5 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-md bg-amber-100 p-1.5 text-amber-700">
                <Info className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-amber-950">
                  {t('dashboard.aliasUpgradeBanner.overFreePlan')}
                </p>
                <p className="text-sm text-amber-800">
                  {t('dashboard.aliasUpgradeBanner.upgradeUnlimited')}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() => setIsPlanModalOpen(true)}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                {t('dashboard.aliasUpgradeBanner.upgradeNow')}
              </button>
              <button
                onClick={() => setShowAliasUpgradeBanner(false)}
                className="inline-flex items-center rounded-md px-2.5 py-1.5 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-100"
              >
                {t('dashboard.aliasUpgradeBanner.maybeLater')}
              </button>
            </div>
          </div>
        </div>
      )}

      {aliasUpgradeTier === 'c' && showAliasUpgradeBanner && (
        <div className="mb-6 rounded-lg border border-sky-200 bg-sky-50 px-5 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-sky-100 p-1.5 text-sky-700">
                <Info className="h-4 w-4" />
              </div>
              <p className="text-sm font-medium text-sky-900">
                {t('dashboard.aliasUpgradeBanner.manageUnlimited')}
              </p>
            </div>
            <button
              onClick={() => setShowAliasUpgradeBanner(false)}
              className="shrink-0 rounded-md px-2 py-1 text-sm font-medium text-sky-700 transition-colors hover:bg-sky-100"
            >
              {t('common.dismiss', 'Dismiss')}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {summaryCards.map((card, index) => (
          <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                {card.icon}
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-500">{card.label}</h3>
            <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 mb-6">
        {usageCards.map((card) => (
          <div key={card.title} className="bg-white p-6 rounded-xl shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {card.title}
            </h2>
            <div className="mb-2 flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600">
                {t('dashboard.stats.quotaUsed', { used: card.used, total: card.total || 0 })}
              </span>
              <span className={`text-sm font-medium ${textColors[card.color]}`}>
                {card.percentage}%
              </span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-gray-200">
              <div
                className={`${progressBarColors[card.color]} h-2.5 rounded-full transition-all duration-300`}
                style={{ width: `${card.percentage}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      {userInfo?.domains && userInfo.domains.length > 0 && (
        <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('dashboard.stats.customDomains')}
          </h2>
          <div className="space-y-2">
            {userInfo.domains.map((domain: string, index: number) => (
              <div key={index} className="flex items-center space-x-2 text-gray-700">
                <AtSign className="h-4 w-4 text-blue-600" />
                <span>{domain}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('dashboard.latestActivities', '最新动态')}
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('activities.table.time', '时间')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('activities.table.type', '类型')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('activities.table.alias', '收件人')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('activities.table.sender', '发件人')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('activities.table.recipient', '真实邮箱')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {processedActivities.map((activity, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(activity.occurred_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center gap-1.5">
                      {activity._hasCheck && <Check className="h-4 w-4 text-green-500 mr-1" />}
                      {activity._displayLabel}
                      {activity.meta && (
                        <span
                          title={formatActivityDetails(activity)}
                          aria-label={t('activities.table.details', '详情')}
                          className="inline-flex"
                        >
                          <Info className="h-4 w-4 text-gray-400" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {activity.alias_email || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatActivitySender(activity)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {activity.direction === 'inbound' ? (activity.delivered_to_email || '-') : 
                     activity.direction === 'outbound' ? (activity.replied_to_email || '-') : '-'}
                  </td>
                </tr>
              ))}
              {processedActivities.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">{t('activities.empty', '暂无动态')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <a 
        href="https://commonapi.mxfast.com/static/feedback/?app=uumail" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
      >
        <Mail className="h-6 w-6" />
      </a>
    </DashboardLayout>
  );
};

export default Dashboard;
